export type RuntimeQrStyle = 'rounded' | 'dots' | 'square' | 'extra-rounded';
export type RuntimeQrShape = 'square' | 'circle';
export type RuntimeQrImageFormat = 'svg';
export type RuntimeQrIconKind = 'preset' | 'url' | 'dataUri' | 'base64';
export type RuntimeQrTheme = 'default' | 'dark-transparent';
export type RuntimeQrIconMimeType = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/svg+xml';
export type RuntimeQrIconPreset =
  | 'logo-shield'
  | 'logo-shield-proximo2'
  | 'logo-shield-qrcode'
  | 'logo-shield-qrcode-backing';

export type RuntimeQrRequest = {
  value: string;
  theme?: RuntimeQrTheme;
  size?: number;
  width?: number;
  height?: number;
  shape?: RuntimeQrShape;
  style?: RuntimeQrStyle;
  icon?: string;
  iconKind?: RuntimeQrIconKind;
  iconMimeType?: RuntimeQrIconMimeType;
  iconSize?: number;
  hideBackgroundDots?: boolean;
  centerLabel?: string;
  centerLabelWidth?: number;
  centerLabelHeight?: number;
  centerLabelFontSize?: number;
  centerLabelForeground?: string;
  centerLabelBackground?: string;
  foreground?: string;
  background?: string;
  margin?: number;
  format?: RuntimeQrImageFormat;
};

export type RuntimeQrResolvedOptions = Required<Omit<RuntimeQrRequest, 'icon' | 'iconKind' | 'iconMimeType' | 'centerLabel'>> & {
  icon?: string;
  iconKind?: RuntimeQrIconKind;
  iconMimeType?: RuntimeQrIconMimeType;
  centerLabel?: string;
};

export type RuntimeQrRenderResult = {
  options: RuntimeQrResolvedOptions;
  elementId: string;
};

export function resolveRuntimeQrOptions(request: RuntimeQrRequest): RuntimeQrResolvedOptions {
  if (!request.value || typeof request.value !== 'string') {
    throw new Error('QR value is required.');
  }

  const theme = request.theme || 'default';
  const themeDefaults = QR_THEME_DEFAULTS[theme];
  const size = clampNumber(request.size, 64, 640, 240);
  const width = clampNumber(request.width, 64, 640, size);
  const height = clampNumber(request.height, 64, 640, size);
  const minSide = Math.min(width, height);
  const iconSize = clampNumber(request.iconSize, 0, Math.round(minSide * 0.5), 25);
  const icon = resolveRuntimeQrIcon({
    icon: request.icon ?? themeDefaults.icon,
    iconKind: request.iconKind ?? themeDefaults.iconKind,
    iconMimeType: request.iconMimeType ?? themeDefaults.iconMimeType,
  });
  const centerLabel = request.centerLabel?.trim() || undefined;

  return {
    value: request.value,
    theme,
    size,
    width,
    height,
    shape: request.shape || themeDefaults.shape || 'circle',
    style: request.style || themeDefaults.style || 'rounded',
    icon,
    iconKind: request.iconKind ?? themeDefaults.iconKind,
    iconMimeType: request.iconMimeType ?? themeDefaults.iconMimeType,
    iconSize,
    hideBackgroundDots: request.hideBackgroundDots ?? themeDefaults.hideBackgroundDots ?? true,
    centerLabel,
    centerLabelWidth: clampNumber(request.centerLabelWidth, 48, minSide - 12, Math.min(minSide - 18, 132)),
    centerLabelHeight: clampNumber(request.centerLabelHeight, 20, Math.round(minSide * 0.3), 34),
    centerLabelFontSize: clampNumber(request.centerLabelFontSize, 9, 18, 12),
    centerLabelForeground: request.centerLabelForeground || '#1f2937',
    centerLabelBackground: request.centerLabelBackground || '#ffffff',
    foreground: request.foreground || themeDefaults.foreground || '#111827',
    background: request.background ?? themeDefaults.background ?? '#ffffff',
    margin: clampNumber(request.margin ?? themeDefaults.margin, 0, 24, 4),
    format: request.format || 'svg',
  };
}

export function resolveRuntimeQrIcon(request: Pick<RuntimeQrRequest, 'icon' | 'iconKind' | 'iconMimeType'>): string | undefined {
  const rawIcon = request.icon?.trim();
  if (!rawIcon) return undefined;

  const kind = request.iconKind || inferIconKind(rawIcon);

  if (kind === 'preset') {
    return resolveIconPreset(rawIcon);
  }

  if (kind === 'dataUri') {
    if (!isAllowedImageDataUri(rawIcon)) {
      throw new Error('QR icon data URI must be a base64 image.');
    }
    return rawIcon;
  }

  if (kind === 'base64') {
    const base64 = rawIcon.replace(/\s/g, '');
    if (!isBase64Payload(base64)) {
      throw new Error('QR icon base64 payload is invalid.');
    }
    return `data:${request.iconMimeType || 'image/png'};base64,${base64}`;
  }

  if (!isAllowedIconUrl(rawIcon)) {
    throw new Error('QR icon URL must be http(s), root-relative, or a safe local asset path.');
  }

  return rawIcon;
}

export function createRuntimeQrRequest(overrides: RuntimeQrRequest): RuntimeQrResolvedOptions {
  return resolveRuntimeQrOptions(overrides);
}

export function createRuntimeQrElementId(prefix = 'ac-runtime-qr'): string {
  const suffix = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now().toString(36)}-${suffix}`;
}

export function createRuntimeQrResult(request: RuntimeQrRequest): RuntimeQrRenderResult {
  return {
    options: resolveRuntimeQrOptions(request),
    elementId: createRuntimeQrElementId(),
  };
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.round(parsed)));
}

function inferIconKind(icon: string): RuntimeQrIconKind {
  if (icon.startsWith('data:image/')) return 'dataUri';
  if (icon in ICON_PRESETS) return 'preset';
  return 'url';
}

function resolveIconPreset(icon: string): string {
  const resolved = ICON_PRESETS[icon as RuntimeQrIconPreset];
  if (!resolved) {
    throw new Error(`Unknown QR icon preset: ${icon}`);
  }
  return resolved;
}

function isAllowedIconUrl(icon: string): boolean {
  return (
    /^https?:\/\//i.test(icon) ||
    icon.startsWith('/') ||
    icon.startsWith('./') ||
    icon.startsWith('../')
  );
}

function isAllowedImageDataUri(icon: string): boolean {
  return /^data:image\/(?:png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/i.test(icon.replace(/\s/g, ''));
}

function isBase64Payload(icon: string): boolean {
  return icon.length <= 512_000 && /^[A-Za-z0-9+/=]+$/.test(icon);
}

const ICON_PRESETS: Record<RuntimeQrIconPreset, string> = {
  'logo-shield': '/icons/logo-shield.svg',
  'logo-shield-proximo2': '/icons/logo-shield-proximo2.png',
  'logo-shield-qrcode': '/icons/logo-shield-qrcode.png',
  'logo-shield-qrcode-backing': '/icons/logo-shield-qrcode-backing.svg',
};

const QR_THEME_DEFAULTS: Record<RuntimeQrTheme, Partial<RuntimeQrRequest>> = {
  default: {},
  'dark-transparent': {
    shape: 'square',
    style: 'rounded',
    icon: 'logo-shield-qrcode-backing',
    iconKind: 'preset',
    hideBackgroundDots: false,
    foreground: '#ffffff',
    background: 'transparent',
    margin: 0,
  },
};
